import { curry, replace, toNumber } from 'lodash/fp';

import { setCharAtIndex } from '@skippay/core/utils/string';

const { round } = Math;
const { isNaN, isFinite } = Number;

/**
 * Returns weather value is number or string containing valid number.
 * @param {any} value
 * @return {boolean}
 */
export const isNumeric = (value) => !isNaN(parseFloat(value)) && isFinite(Number(value));

/**
 * Parses input to number, for valid number returns stringified number, otherwise empty string.
 * Useful for short circuits (stringifies 0 to `0` thus to truthy value).
 * @param {any} value
 * @return {string}
 */
export const parseToString = (value) => {
	const parsed = parseFloat(value);
	return !isNaN(parsed) && isFinite(Number(value)) ? String(parsed) : ``;
};

/**
 * Compares same parsed value. E.g. `000` is same as `0` and all NaN are same.
 * @param {any} a
 * @param {any} b
 * @return {boolean}
 */
export const sameParsedValue = (a, b) => parseToString(a) === parseToString(b);

/**
 * Rounds number to specified magnitude. Rounding is otherwise arithmetic.
 * @example
 * 	roundTo(1000, 12400) => 12000
 * @param {integer} magnitude
 * @param {number} num
 */
export const roundTo = curry((magnitude, num) =>
	magnitude === 0 ? round(num) : round(num / magnitude) * magnitude
);

const numberAndSeparatorOnly = replace(/[^\d.]/g, '');
const fixCommaSeparator = (value) => {
	if (value) {
		const commaSeparatorIdx = value.lastIndexOf(',');
		const fullStopSeparatorIdx = value.lastIndexOf('.');
		if (commaSeparatorIdx > fullStopSeparatorIdx) {
			if (fullStopSeparatorIdx > -1) {
				const pointLessValue = value.replace(/\./g, ',');
				return setCharAtIndex(pointLessValue, commaSeparatorIdx, '.');
			}
			return setCharAtIndex(value, commaSeparatorIdx, '.');
		}
	}
	return value;
};

/**
 * Converts string to number, spits input out if it contains non-valid number. Handles user input,
 * converts decimal comma to decimal point and handles various thousands separators in input
 * @param {string} value
 * @return {number | any}
 */
export const parseNumber = (value) => {
	if (!value) return value;
	const numbersOnly = numberAndSeparatorOnly(fixCommaSeparator(value));
	if (!numbersOnly || numbersOnly === '.') return '';
	return toNumber(`${value[0] === '-' ? '-' : ''}${numbersOnly}`);
};

/**
 * Returns number of decimal places (a.k.a. scale) of the number
 * @param {number} value
 * @return {number}
 */
export const getDecimalPlaces = (value) => (value && String(value).split('.', 2)[1]?.length) || 0;

/**
 * Functional version of "+" operator which handles IEEE 754 quirks.
 * NOTE: JS uses IEEE 754 representation of numbers which has problems with certain floating point
 * numbers, e.g. "2.123 - 1 => 1.1230000000000002", it handles integers without quirks, so this
 * function converts floats to integers before addition and then converts it back.
 * This function is very slow and only needed when results of computation are shown to the user.
 * @see https://en.wikipedia.org/wiki/Double-precision_floating-point_format
 * @param {number} a
 * @param {number} b
 * @return {number}
 */
export const add = curry((a, b) => {
	const maxPrecision = Math.max(getDecimalPlaces(a), getDecimalPlaces(b));
	if (maxPrecision === 0) return Number(a) + Number(b);
	const multiplier = 10 ** maxPrecision;
	return (a * multiplier + b * multiplier) / multiplier;
});

/**
 * Returns decimal separator depending for the selected locale
 * @param {string} locale from Intl
 * @returns {string}
 */
export const getDecimalSeparator = (locale) => {
	const numberWithDecimalSeparator = 1.1;
	return Intl.NumberFormat(locale)
		.formatToParts(numberWithDecimalSeparator)
		.find((part) => part.type === 'decimal').value;
};
